import {
  MiraException,
  MiraExceptionSave,
  MiraExceptionSuggestion,
  MiraLateEvent,
} from "@/data/models/MiraExceptionAdministration";

/**
 * The logic behind the Mira-Ausnahmen screen of the IRES Admin Client (Hans, Track #2610040 R8), kept out
 * of the components so it can be tested without mounting anything. IRES Cloud is the authority
 * (`MiraExceptionsForAdministration.ts`); these rules give the messages before a save is sent.
 */

/** The list's filters. */
export const MIRA_EXCEPTION_FILTERS = ["ACTIVE", "RETIRED", "ALL"] as const;
export type MiraExceptionFilter = typeof MIRA_EXCEPTION_FILTERS[number];

/** The dialog's editable fields. Days are `YYYY-MM-DD`, empty while not chosen. */
export interface MiraExceptionForm {
  clientId: string;
  event: MiraLateEvent | "";
  from: string;
  to: string;
  reason: string;
  retired: boolean;
}

/** The form for an entry, or an empty one for a new entry. */
export function toExceptionForm(entry: MiraException | null): MiraExceptionForm {
  return {
    clientId: entry?.clientId ?? "",
    event: entry?.event ?? "",
    from: entry?.from ?? "",
    to: entry?.to ?? "",
    reason: entry?.reason ?? "",
    retired: entry?.retired ?? false,
  };
}

/** Whether the form differs from the stored entry. */
export function exceptionFormChanged(stored: MiraException | null, form: MiraExceptionForm): boolean {
  const before = toExceptionForm(stored);
  return (
    form.clientId !== before.clientId ||
    form.event !== before.event ||
    form.from !== before.from ||
    form.to !== before.to ||
    form.reason.trim() !== before.reason ||
    form.retired !== before.retired
  );
}

/** A field's problem, as an i18n key below `admin.miraExceptions.fieldErrors`. */
export function exceptionFieldError(field: "clientId" | "event" | "range" | "reason", form: MiraExceptionForm): string | null {
  if (field === "clientId") return form.clientId ? null : "required";
  if (field === "event") return form.event ? null : "required";
  if (field === "reason") return form.reason.trim() ? null : "required";
  if (!form.from || !form.to) return "required";
  return form.from <= form.to ? null : "range";
}

/** Whether the form can be saved. */
export function canSaveExceptionForm(form: MiraExceptionForm): boolean {
  return (["clientId", "event", "range", "reason"] as const).every((field) => exceptionFieldError(field, form) === null);
}

/** What a save sends. Call only when {@link canSaveExceptionForm}. */
export function toExceptionSave(stored: MiraException | null, form: MiraExceptionForm): MiraExceptionSave {
  return {
    id: stored?.id ?? null,
    clientId: form.clientId,
    event: form.event as MiraLateEvent,
    from: form.from,
    to: form.to,
    reason: form.reason.trim(),
    retired: form.retired,
    expectedUpdatedAt: stored?.updatedAt ?? null,
  };
}

/** A local calendar day as `YYYY-MM-DD`, the stored form. */
export function dayOf(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** The local midnight of a stored day, or null for none. */
export function dateOfDay(day: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])) : null;
}

/** The list rows for a filter and a search over the Mandant and the reason, newest range first. */
export function filterExceptions(
  entries: MiraException[],
  filter: MiraExceptionFilter,
  search: string,
  labelOf: (clientId: string) => string
): MiraException[] {
  const term = search.trim().toLowerCase();
  return entries
    .filter((entry) => (filter === "ALL" ? true : filter === "RETIRED" ? entry.retired : !entry.retired))
    .filter((entry) => !term || [labelOf(entry.clientId), entry.reason].some((value) => value.toLowerCase().includes(term)))
    .sort((a, b) => b.from.localeCompare(a.from) || labelOf(a.clientId).localeCompare(labelOf(b.clientId), "de"));
}

/** The snackbar message for a refused or failed call, by IRES Cloud's reason. */
export function exceptionErrorMessage(error: unknown): { key: string; params: string[] } {
  const failure = (error ?? {}) as { details?: { reason?: unknown } | null };
  const reason = typeof failure.details?.reason === "string" ? failure.details.reason : "";
  const known = ["NO_EXCEPTION", "NO_CLIENT", "CHANGED_MEANWHILE", "INVALID_EVENT", "INVALID_RANGE", "INVALID_REASON"];
  return { key: known.includes(reason) ? `admin.miraExceptions.errors.${reason}` : "admin.miraExceptions.errors.FAILED", params: [] };
}

/** The longest reason IRES Cloud accepts, for an entry and a dismissal alike. */
export const MAX_REASON_LENGTH = 500;

/** The values a taken-over suggestion fills into a new entry's form. */
export type MiraExceptionPrefill = Pick<MiraExceptionForm, "clientId" | "event" | "from" | "to">;

/** ÜBERNEHMEN: the Mandant, event and days of a suggestion, so only the reason is left to type. */
export function suggestionPrefill(suggestion: MiraExceptionSuggestion): MiraExceptionPrefill {
  return { clientId: suggestion.clientId, event: suggestion.event, from: suggestion.from, to: suggestion.to };
}

/** The form of a new entry, filled with a prefill when there is one. */
export function toNewExceptionForm(prefill: MiraExceptionPrefill | null): MiraExceptionForm {
  return { ...toExceptionForm(null), ...(prefill ?? {}) };
}

/** The suggestions for a search over the Mandant, newest days first. */
export function filterSuggestions(
  suggestions: MiraExceptionSuggestion[],
  search: string,
  labelOf: (clientId: string) => string
): MiraExceptionSuggestion[] {
  const term = search.trim().toLowerCase();
  return suggestions
    .filter((suggestion) => !term || labelOf(suggestion.clientId).toLowerCase().includes(term))
    .sort((a, b) => b.from.localeCompare(a.from) || labelOf(a.clientId).localeCompare(labelOf(b.clientId), "de"));
}

/** One Mandant's suggestions, as the screen groups them. */
export interface MiraSuggestionGroup {
  clientId: string;
  label: string;
  suggestions: MiraExceptionSuggestion[];
}

/**
 * The suggestions by Mandant, by name, each group keeping the order it is given (newest days first from
 * {@link filterSuggestions}).
 */
export function groupSuggestions(suggestions: MiraExceptionSuggestion[], labelOf: (clientId: string) => string): MiraSuggestionGroup[] {
  const groups = new Map<string, MiraSuggestionGroup>();
  for (const suggestion of suggestions) {
    const group = groups.get(suggestion.clientId) ?? { clientId: suggestion.clientId, label: labelOf(suggestion.clientId), suggestions: [] };
    group.suggestions.push(suggestion);
    groups.set(suggestion.clientId, group);
  }
  return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label, "de"));
}

/** Whether a dismissal's reason can be sent. */
export function canDismissWithReason(reason: string): boolean {
  const trimmed = reason.trim();
  return trimmed.length > 0 && trimmed.length <= MAX_REASON_LENGTH;
}

/** The snackbar message for a refused or failed dismissal, by IRES Cloud's reason. */
export function suggestionErrorMessage(error: unknown): { key: string; params: string[] } {
  const failure = (error ?? {}) as { details?: { reason?: unknown } | null };
  const reason = typeof failure.details?.reason === "string" ? failure.details.reason : "";
  const known = ["NO_SUGGESTION", "CHANGED_MEANWHILE", "INVALID_REASON"];
  return { key: known.includes(reason) ? `admin.miraExceptions.suggestions.errors.${reason}` : "admin.miraExceptions.suggestions.errors.FAILED", params: [] };
}
